import {Box, Button, Card, CardContent, CircularProgress, Grid, IconButton, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {useCallback, useEffect, useRef, useState} from "react";
import {QrReader} from "react-qr-reader";
import Webcam from "react-webcam";
import {useNotify} from "react-admin";

import dataprovider, {client} from "../dataProvider";
import ReplayIcon from "@material-ui/icons/Replay";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import SendIcon from "@material-ui/icons/Send";
import AttachmentIcon from "@material-ui/icons/Attachment";
import DescriptionIcon from "@material-ui/icons/Description";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import MyLocationIcon from "@material-ui/icons/MyLocation";
import QrCodeIcon from "@material-ui/icons/CropFree";

const videoConstraints = {
  width: 800,
  height: 1200,
  facingMode: "environment",
  peerIdentity: "user",
};

type UploadType = "note" | "attachment";

const useStyles = makeStyles((theme) => ({
  page: {minHeight: "calc(100vh - 64px)", padding: theme.spacing(2), backgroundColor: theme.palette.background.default},
  scannerCard: {width: "100%", maxWidth: 560, margin: "0 auto"},
  cardContent: {padding: theme.spacing(2), "&:last-child": {paddingBottom: theme.spacing(3)}},
  header: {display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(2)},
  subtitle: {color: theme.palette.text.secondary},
  statusRow: {marginBottom: theme.spacing(3)},
  status: {display: "flex", alignItems: "center", gap: theme.spacing(1), minHeight: 52, padding: theme.spacing(1, 1.5), borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.grey[100]},
  statusReady: {color: theme.palette.success ? theme.palette.success.main : "#2e7d32"},
  statusWaiting: {color: theme.palette.error.main},
  qrFrame: {
    width: "100%", maxWidth: 360, margin: "0 auto", overflow: "hidden", borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.grey[100],
    "& video": {display: "block", width: "100% !important", height: "auto !important"},
  },
  choiceButton: {width: "100%", minHeight: 130, display: "flex", flexDirection: "column", gap: theme.spacing(1)},
  choiceIcon: {fontSize: 36},
  captureFrame: {
    width: "100%", maxWidth: 360, height: 400, margin: "0 auto", overflow: "hidden", borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.grey[100],
    "& video": {width: "100% !important", height: "100% !important", objectFit: "cover"},
    [theme.breakpoints.down("xs")]: {height: 360},
  },
  preview: {display: "block", width: "100%", maxWidth: 360, maxHeight: 430, margin: "0 auto", objectFit: "contain", borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.grey[100]},
  actionRow: {marginTop: theme.spacing(2)},
  loadingOverlay: {position: "fixed", inset: 0, zIndex: theme.zIndex.modal + 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: theme.spacing(2), color: theme.palette.common.white, backgroundColor: "rgba(0, 0, 0, 0.55)"},
}));

const WebcamCapture = (props: {onCapture: (image: string | null) => void, uploadType: UploadType, className: string}) => {
  const webcamRef = useRef<any>(null);
  const capture = useCallback(() => props.onCapture(webcamRef.current?.getScreenshot() || null), [props]);

  return <>
    <div className={props.className}>
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat={props.uploadType === "attachment" ? "image/jpeg" : "image/png"}
        screenshotQuality={0.75}
        videoConstraints={videoConstraints}
        minScreenshotHeight={1200}
        minScreenshotWidth={800}
        forceScreenshotSourceSize={true}
        disablePictureInPicture={true}
        imageSmoothing={true}
        mirrored={false}
        onUserMediaError={(error: string | DOMException) => window.alert(error)}
      />
    </div>
    <Box mt={2} textAlign="center">
      <Button variant="contained" color="primary" startIcon={<PhotoCameraIcon />} onClick={capture}>Scatta foto</Button>
    </Box>
  </>;
};

interface LocationOptions {enableHighAccuracy?: boolean; timeout?: number; maximumAge?: number;}
interface LocationState {
  coords: {latitude: number | null; longitude: number | null; accuracy: number | null; altitude: number | null; altitudeAccuracy: number | null; heading: number | null; speed: number | null;};
  locatedAt: number | null;
  error: string | null;
}

const useLocation = (options: LocationOptions = {}) => {
  const [location, setLocation] = useState<LocationState>({
    coords: {latitude: null, longitude: null, accuracy: null, altitude: null, altitudeAccuracy: null, heading: null, speed: null},
    locatedAt: null,
    error: null,
  });

  useEffect(() => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) {
      setLocation((previousState) => ({...previousState, error: "La geolocalizzazione non è disponibile nel browser"}));
      return;
    }
    const watcher = navigator.geolocation.watchPosition(
      (position) => setLocation({
        coords: {
          latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy: position.coords.accuracy,
          altitude: position.coords.altitude, altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading, speed: position.coords.speed,
        },
        locatedAt: position.timestamp,
        error: null,
      }),
      (error) => setLocation((previousState) => ({...previousState, error: error.message})),
      {enableHighAccuracy: options.enableHighAccuracy || false, timeout: options.timeout || Infinity, maximumAge: options.maximumAge || 0},
    );
    return () => navigator.geolocation.clearWatch(watcher);
  }, [options.enableHighAccuracy, options.timeout, options.maximumAge]);

  return location;
};

export const QrScanner = () => {
  const classes = useStyles();
  const delivery = new URLSearchParams(window.location.search).get("delivery");
  const notify = useNotify();
  const location = useLocation();
  const [scanActive, setScanActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState<UploadType | null>(null);
  const hasLocation = Boolean(location.coords.latitude && location.coords.longitude);
  const isAttachment = uploadType === "attachment";

  const resetView = () => { window.location.href = "/qr_scanner/"; };
  const selectUploadType = (type: UploadType | null) => { setUploadType(type); setImgSrc(null); };

  const sendImage = useCallback(() => {
    if (!imgSrc || !delivery || !uploadType) {
      notify("Scatta una foto prima di inviare", {type: "error"});
      return;
    }
    if (uploadType === "note" && !hasLocation) {
      notify("Attendere la posizione GPS prima di inviare la bolla", {type: "error"});
      return;
    }
    setLoading(true);
    const attachment = uploadType === "attachment";
    const data = attachment
      ? {file: imgSrc, delivery: parseInt(delivery.replace("S.", ""), 10)}
      : {file: imgSrc, config_name: "", final: true, delivery: parseInt(delivery.replace("S.", ""), 10), origin: "D", lat_lon: `${location.coords.latitude},${location.coords.longitude}`};

    dataprovider("/api").create(attachment ? "delivery_attachments" : "notes", {data})
      .then(() => { notify(attachment ? "Allegato inviato" : "Bolla inviata", {type: "success"}); resetView(); })
      .catch((error: any) => { console.log(error); notify("Errore durante l'invio dell'immagine", {type: "error"}); })
      .finally(() => setLoading(false));
  }, [delivery, hasLocation, imgSrc, location.coords.latitude, location.coords.longitude, notify, uploadType]);

  const scanQr = (result: any) => {
    if (!scanActive || !result?.text) return;
    setScanActive(false);
    setLoading(true);
    client(`./deliveries/qrscanner/?qr=${result.text}`, {method: "GET"})
      .then(() => { window.location.href = `/qr_scanner/?delivery=${result.text}`; })
      .catch(() => { setScanActive(true); notify("QR non valido o missione non trovata", {type: "error"}); })
      .finally(() => setLoading(false));
  };

  return <div className={classes.page}>
    {loading && <div className={classes.loadingOverlay}><CircularProgress color="inherit" /><Typography variant="h6">Caricamento...</Typography></div>}
    <Card className={classes.scannerCard} elevation={2}>
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div>
            <Typography variant="h5">Scanner missione</Typography>
            <Typography variant="body2" className={classes.subtitle}>{delivery ? `Missione ${delivery}` : "Inquadra il QR della missione"}</Typography>
          </div>
          <IconButton aria-label="Nuova scansione" onClick={resetView} color="primary"><ReplayIcon /></IconButton>
        </div>

        <Grid container spacing={1} className={classes.statusRow}>
          <Grid item xs={12} sm={6}><div className={`${classes.status} ${hasLocation ? classes.statusReady : classes.statusWaiting}`}><MyLocationIcon /><Typography variant="body2">{hasLocation ? "GPS disponibile" : "Ricerca GPS..."}</Typography></div></Grid>
          <Grid item xs={12} sm={6}><div className={`${classes.status} ${delivery ? classes.statusReady : classes.statusWaiting}`}><QrCodeIcon /><Typography variant="body2">{delivery ? `Missione ${delivery}` : "Missione da acquisire"}</Typography></div></Grid>
        </Grid>

        {!delivery ? <div className={classes.qrFrame}><QrReader onResult={scanQr} constraints={{facingMode: "environment"}} /></div>
          : uploadType === null ? <>
            <Typography variant="h6" align="center" gutterBottom>Cosa vuoi caricare?</Typography>
            <Typography variant="body2" align="center" className={classes.subtitle}>Scegli il tipo prima di scattare la foto.</Typography>
            <Grid container spacing={2} style={{marginTop: 8}}>
              <Grid item xs={12} sm={6}><Button className={classes.choiceButton} variant="contained" color="primary" onClick={() => selectUploadType("note")}><DescriptionIcon className={classes.choiceIcon} />Carica bolla</Button></Grid>
              <Grid item xs={12} sm={6}><Button className={classes.choiceButton} variant="outlined" color="primary" onClick={() => selectUploadType("attachment")}><AttachmentIcon className={classes.choiceIcon} />Carica allegato</Button></Grid>
            </Grid>
          </> : <>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <div><Typography variant="h6">{isAttachment ? "Foto allegato" : "Foto bolla"}</Typography><Typography variant="body2" className={classes.subtitle}>{isAttachment ? "Foto compressa per un caricamento veloce" : "Il GPS è necessario per inviare la bolla"}</Typography></div>
              <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => selectUploadType(null)}>Indietro</Button>
            </Box>
            {imgSrc ? <>
              <img src={imgSrc} alt="Anteprima della foto" className={classes.preview} />
              <Grid container spacing={1} className={classes.actionRow}>
                <Grid item xs={12} sm={6}><Button fullWidth variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => setImgSrc(null)}>Scatta di nuovo</Button></Grid>
                <Grid item xs={12} sm={6}><Button fullWidth variant="contained" color="primary" startIcon={<SendIcon />} onClick={sendImage}>Invia {isAttachment ? "allegato" : "bolla"}</Button></Grid>
              </Grid>
            </> : <WebcamCapture className={classes.captureFrame} uploadType={uploadType} onCapture={setImgSrc} />}
          </>}
      </CardContent>
    </Card>
  </div>;
};
